'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { DisconnectReason, Room, RoomEvent } from 'livekit-client';
import { motion } from 'motion/react';
import { RoomContext } from '@livekit/components-react';
import { ErrorMessage } from '@/components/embed-popup/error-message';
import { PopupView } from '@/components/embed-popup/popup-view';
import { Trigger } from '@/components/embed-popup/trigger';
import useConnectionDetails from '@/hooks/use-connection-details';
import useConversationPersistence from '@/hooks/use-conversation-persistence';
import { getPopupState, loadSession, setPopupState } from '@/lib/session-storage';
import { getTracker } from '@/lib/tracker';
// Get the API base URL for sendBeacon calls
function getApiBaseUrl() {
    if (typeof window !== 'undefined') {
        const scriptTag = document.querySelector('script[data-lk-sandbox-id]');
        if (scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.src) {
            try {
                return new URL(scriptTag.src).origin;
            }
            catch (_a) {
                // fall through
            }
        }
    }
    return typeof window !== 'undefined' ? window.location.origin : '';
}
const PopupViewMotion = motion.create(PopupView);
function AgentClient({ appConfig }) {
    const isAnimating = useRef(false);
    const isClosing = useRef(false);
    const room = useMemo(() => new Room(), []);
    const [popupOpen, setPopupOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [error, setError] = useState(null);
    const [pendingMessage, setPendingMessage] = useState(null);
    const [emailCollected, setEmailCollected] = useState(false);
    const [skippedEmailForm, setSkippedEmailForm] = useState(false);
    const [contactBannerDismissed, setContactBannerDismissed] = useState(false);
    const [showResumePrompt, setShowResumePrompt] = useState(false);
    const resumeCheckDone = useRef(false);
    const hasActiveSession = useMemo(() => !!loadSession(appConfig.webWidgetId), [appConfig.webWidgetId]);
    const { restoredMessages, refreshConnectionDetails, existingOrRefreshConnectionDetails, clearCurrentSession, } = useConnectionDetails(appConfig);
    const { conversationId, visitorId, dbMessages, isFollowup: isConversationResumed, visitorEmail, visitorPhone, visitorName, setVisitorInfo, setVisitorContact, recheckForIncomplete, clearFollowup, } = useConversationPersistence(appConfig, hasActiveSession);
    // Determine if we need to show email form.
    // Once the user skips, the form hides and the chat can connect; the small
    // contact banner in the header takes over so they can still share contact
    // info later without blocking the conversation.
    const showEmailForm = !!(appConfig.collectEmail &&
        !visitorEmail &&
        !emailCollected &&
        !skippedEmailForm);
    const showContactBanner = !!(appConfig.collectEmail &&
        skippedEmailForm &&
        !visitorEmail &&
        !visitorPhone &&
        !contactBannerDismissed);
    // Auto-open popup if we have a restored session (page-nav or DB conversation resume).
    // Use hasActiveSession (computed synchronously from sessionStorage) rather than
    // restoredMessages.length, which is still empty on the first render cycle.
    useEffect(() => {
        if (getPopupState(appConfig.webWidgetId) && hasActiveSession) {
            setPopupOpen(true);
        }
        // Only run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Auto-open if a conversation was resumed from DB (user returned after hours)
    useEffect(() => {
        if (isConversationResumed && dbMessages.length > 0 && !hasActiveSession) {
            setPopupOpen(true);
        }
    }, [isConversationResumed, dbMessages.length, hasActiveSession]);
    // Expose window.__zalenaSendMessage for external callers (e.g. demo page "Try Asking" prompts)
    useEffect(() => {
        const w = window;
        w.__zalenaSendMessage = (text) => {
            setPendingMessage(text);
            setError(null);
            if (!popupOpen) {
                setPopupOpen(true);
            }
            if (minimized) {
                setMinimized(false);
            }
        };
        return () => {
            delete w.__zalenaSendMessage;
        };
    }, [popupOpen, minimized]);
    // Toggle popup open/closed from the trigger button
    const handleTogglePopup = () => {
        var _a;
        if (isAnimating.current) {
            return;
        }
        if (minimized) {
            // Restore from minimized — session is still alive
            setMinimized(false);
            (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track('chat_restored');
            return;
        }
        setError(null);
        setPopupOpen((open) => {
            var _a;
            const newState = !open;
            setPopupState(newState, appConfig.webWidgetId);
            (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track(newState ? 'chat_opened' : 'chat_closed');
            // When opening and room is disconnected, check for incomplete conversations
            if (newState && room.state === 'disconnected' && !resumeCheckDone.current) {
                resumeCheckDone.current = true;
                recheckForIncomplete().then((found) => {
                    if (found) {
                        setShowResumePrompt(true);
                    }
                });
            }
            return newState;
        });
    };
    // Handle resume prompt choice
    const handleResumeChoice = (choice) => {
        var _a;
        if (choice === 'fresh') {
            clearFollowup();
        }
        setShowResumePrompt(false);
        (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track('conversation_resume', { choice });
    };
    // Minimize — just hide the window, keep the session alive
    const handleMinimize = () => {
        var _a;
        setMinimized(true);
        (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track('chat_minimized');
    };
    // Close — end the session and disconnect (agent handles DB status via shutdown callback)
    const handleClose = () => {
        isClosing.current = true;
        setMinimized(false);
        setPopupOpen(false);
        clearCurrentSession();
    };
    // Handle email form submission
    const handleEmailSubmit = (email, name) => {
        var _a;
        setVisitorInfo(email, name);
        setEmailCollected(true);
        (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track('email_submitted');
    };
    // User chose to skip the email gate; let the chat connect anonymously.
    const handleEmailSkip = () => {
        var _a;
        setSkippedEmailForm(true);
        (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track('email_skipped');
    };
    // Passive contact capture via the header banner (after skip).
    const handleContactSubmit = (contact) => {
        var _a, _b;
        setVisitorContact(contact);
        setContactBannerDismissed(true);
        const tracker = getTracker();
        if (contact.email)
            tracker === null || tracker === void 0 ? void 0 : tracker.track('email_submitted');
        if (contact.phone)
            tracker === null || tracker === void 0 ? void 0 : tracker.track('phone_submitted');
        // If the LiveKit room is already live, publish the new contact info via
        // data channel so the agent picks it up immediately — otherwise it would
        // only surface on the next session (through the connection-details token).
        if (room.state === 'connected') {
            try {
                const payload = JSON.stringify({
                    type: 'visitor_contact',
                    email: (_a = contact.email) !== null && _a !== void 0 ? _a : null,
                    phone: (_b = contact.phone) !== null && _b !== void 0 ? _b : null,
                });
                const bytes = new TextEncoder().encode(payload);
                // Reliable delivery so the agent doesn't miss the update.
                room.localParticipant
                    .publishData(bytes, { reliable: true, topic: 'visitor_contact' })
                    .catch((err) => {
                    console.warn('[zalena-embed] failed to publish visitor_contact:', err);
                });
            }
            catch (err) {
                console.warn('[zalena-embed] failed to encode visitor_contact payload:', err);
            }
        }
    };
    const handleContactDismiss = () => {
        var _a;
        setContactBannerDismissed(true);
        (_a = getTracker()) === null || _a === void 0 ? void 0 : _a.track('contact_banner_dismissed');
    };
    const handlePanelAnimationStart = () => {
        isAnimating.current = true;
    };
    const handlePanelAnimationComplete = () => {
        isAnimating.current = false;
        if (isClosing.current && room.state !== 'disconnected') {
            room.disconnect();
            isClosing.current = false;
        }
    };
    // Send "page_navigating" signal on beforeunload so the agent knows to wait
    // for reconnection instead of ending the session immediately.
    // IMPORTANT: Always send the beacon when a session exists, even if the room
    // hasn't finished connecting yet (covers rapid page A → B → C navigation).
    useEffect(() => {
        const onBeforeUnload = () => {
            const session = loadSession(appConfig.webWidgetId);
            if (!session)
                return;
            const payload = JSON.stringify({
                room_name: session.roomName,
                participant_identity: session.participantIdentity,
            });
            // 1. Try LiveKit data channel (fastest — may arrive before disconnect).
            //    Only possible when the room is fully connected.
            if (room.state === 'connected') {
                try {
                    const encoder = new TextEncoder();
                    const data = encoder.encode(JSON.stringify({ type: 'page_navigating' }));
                    room.localParticipant.publishData(data, {
                        reliable: false,
                        topic: 'navigation_signal',
                    });
                }
                catch (_a) {
                    // WebSocket may already be closing
                }
            }
            // 2. sendBeacon as reliable backup (guaranteed delivery by browser).
            //    Works regardless of room connection state.
            try {
                const url = `${getApiBaseUrl()}/api/signal-navigation`;
                navigator.sendBeacon(url, payload);
            }
            catch (_b) {
                // sendBeacon unavailable
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [room, appConfig.webWidgetId]);
    useEffect(() => {
        const onDisconnected = (reason) => {
            // Only act on server-initiated disconnects (room deleted, participant removed).
            // CLIENT_INITIATED disconnects happen during:
            //   - page navigation (we want to reconnect, not close)
            //   - agent-gone fallback reconnect (we're about to connect to a fresh room)
            //   - handleClose (which already sets popupOpen=false and clears session)
            // In all those cases, closing the popup or clearing the session here would
            // either be redundant or break the reconnection flow.
            if (reason !== undefined && reason !== DisconnectReason.CLIENT_INITIATED) {
                clearCurrentSession();
                setPopupOpen(false);
                resumeCheckDone.current = false;
            }
        };
        const onMediaDevicesError = (error) => {
            setError({
                title: 'Encountered an error with your media devices',
                description: `${error.name}: ${error.message}`,
            });
        };
        room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.Disconnected, onDisconnected);
            room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        };
    }, [room, clearCurrentSession]);
    useEffect(() => {
        if (!popupOpen) {
            return;
        }
        if (room.state !== 'disconnected') {
            return;
        }
        // Don't connect until email is collected (if collection is required).
        // Once the user submits the form, showEmailForm becomes false and this re-runs.
        if (showEmailForm) {
            return;
        }
        // Don't connect until user chooses to continue or start fresh.
        if (showResumePrompt) {
            return;
        }
        const connect = async () => {
            const existingSession = loadSession(appConfig.webWidgetId);
            let details;
            if (existingSession) {
                // Reconnecting to existing session — the agent is waiting (up to 5 min)
                try {
                    details = await existingOrRefreshConnectionDetails();
                    await room.connect(details.serverUrl, details.participantToken);
                    // Verify the agent is in the room. If the agent already left
                    // (reconnect window expired), disconnect and fall through to
                    // create a fresh room below.
                    const hasAgent = Array.from(room.remoteParticipants.values()).some((p) => { var _a; return (_a = p.permissions) === null || _a === void 0 ? void 0 : _a.canPublish; });
                    if (!hasAgent) {
                        // Wait briefly — agent might still be joining
                        const agentArrived = await new Promise((resolve) => {
                            const timeout = setTimeout(() => {
                                room.off(RoomEvent.ParticipantConnected, onJoin);
                                resolve(false);
                            }, 5000);
                            const onJoin = () => {
                                clearTimeout(timeout);
                                resolve(true);
                            };
                            room.once(RoomEvent.ParticipantConnected, onJoin);
                        });
                        if (!agentArrived) {
                            // Agent is gone — disconnect and create fresh room
                            room.disconnect();
                            clearCurrentSession();
                            details = await refreshConnectionDetails({
                                conversationId: conversationId || undefined,
                                visitorId,
                                visitorEmail: visitorEmail || undefined,
                                visitorPhone: visitorPhone || undefined,
                                visitorName: visitorName || undefined,
                                isFollowup: isConversationResumed,
                            });
                            await room.connect(details.serverUrl, details.participantToken);
                        }
                    }
                    return; // successfully connected
                }
                catch (_a) {
                    // Room is gone or token refresh failed — fall through to fresh
                    clearCurrentSession();
                }
            }
            // Fresh session — include conversation metadata in JWT
            details = await refreshConnectionDetails({
                conversationId: conversationId || undefined,
                visitorId,
                visitorEmail: visitorEmail || undefined,
                visitorPhone: visitorPhone || undefined,
                visitorName: visitorName || undefined,
                isFollowup: isConversationResumed,
            });
            await room.connect(details.serverUrl, details.participantToken);
        };
        connect().catch((error) => {
            if (error instanceof Error) {
                console.error('Error connecting to agent:', error);
                setError({
                    title: 'There was an error connecting to the agent',
                    description: `${error.name}: ${error.message}`,
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        room,
        popupOpen,
        showEmailForm,
        showResumePrompt,
        existingOrRefreshConnectionDetails,
        refreshConnectionDetails,
        appConfig.webWidgetId,
    ]);
    return (_jsxs(RoomContext.Provider, { value: room, children: [_jsx(Trigger, { error: error, popupOpen: popupOpen && !minimized, onToggle: handleTogglePopup, appConfig: appConfig }), _jsx(motion.div, { inert: !popupOpen || minimized, initial: {
                    opacity: 0,
                    translateY: 8,
                }, animate: {
                    opacity: popupOpen && !minimized ? 1 : 0,
                    translateY: popupOpen && !minimized ? 0 : 8,
                }, transition: {
                    type: 'spring',
                    bounce: 0,
                    duration: popupOpen && !minimized ? 1 : 0.2,
                }, onAnimationStart: handlePanelAnimationStart, onAnimationComplete: handlePanelAnimationComplete, className: "lk-chat-popup fixed right-[24px] bottom-[24px] z-[9999]", children: _jsx("div", { className: "lk-chat-container ml-auto max-h-[580px] w-[420px] max-w-[calc(100vw-32px)] rounded-lg bg-white shadow-2xl", style: { border: '1px solid #E5E7EB' }, children: _jsxs("div", { className: "relative h-full w-full", children: [_jsx(ErrorMessage, { error: error, chatName: appConfig.chatName }), !error && (_jsx(PopupViewMotion, { appConfig: appConfig, initial: { opacity: 1 }, animate: { opacity: error === null ? 1 : 0 }, transition: {
                                    type: 'spring',
                                    bounce: 0,
                                    duration: 0.2,
                                }, disabled: !popupOpen, sessionStarted: popupOpen, pendingMessage: pendingMessage, restoredMessages: restoredMessages, dbMessages: dbMessages, showEmailForm: showEmailForm, onEmailSubmit: handleEmailSubmit, onEmailSkip: handleEmailSkip, showContactBanner: showContactBanner, onContactSubmit: handleContactSubmit, onContactDismiss: handleContactDismiss, showResumePrompt: showResumePrompt, onResumeChoice: handleResumeChoice, onPendingMessageHandled: () => setPendingMessage(null), onEmbedError: setError, onMinimize: handleMinimize, onClose: handleClose, className: "absolute inset-0" }))] }) }) })] }));
}
export default AgentClient;
